var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
/**
 * CZNA-241: propose-GA-from-picker flow — client-side snapshot store.
 *
 * The picker's "Navrhnout založení GA" affordance no longer hosts the GA
 * ProposalForm in an overlay over the parent editor (that broke the FOLIO
 * Paneset layout — see the removed `<Layer>` block). Instead the parent form's
 * in-progress state is snapshotted to sessionStorage and the app navigates to a
 * SEPARATE create route for the GA. After the GA draft is saved (or cancelled)
 * the parent form rehydrates from the snapshot and, on success, the new GA's FK
 * + heading land in the exact 370 subfield the propose was triggered from.
 *
 * sessionStorage (not a React store / redux) is deliberate:
 *  - it survives SPA navigation AND a hard reload (F5) mid-flow;
 *  - it is tab-scoped, so two tabs running the flow never collide;
 *  - it is JSON-only, which forces the snapshot to be plainly serializable
 *    (no functions / class instances), matching what `getValues()` returns.
 *
 * The store is a strict ONE-SHOT keyed by `crypto.randomUUID()`: `loadSnapshot`
 * deletes the key as it reads, so a re-mount of the same return URL (or a stray
 * second effect run) cannot replay the restore.
 */
import { RecordType } from '../../types/api';
/**
 * CZNA-197 / CZNA-241 / CZNA-319 / CZNA-321: record types whose picker offers
 * "Navrhnout / Založit autoritu …" from the picker.
 *
 * This set is the gate on the TARGET TYPE; the form-config decides on which
 * subfields (`allowFreeText: true`), so e.g. 682 "nahrazeno čím" stays without it
 * although it is a PERSONAL / CORPORATE picker. CORPORATE and MEETING are CZNA-320.
 */
export var PROPOSE_AUTHORITY_TARGETS = new Set([
    RecordType.GEOGRAPHIC,
    RecordType.PERSONAL,
    RecordType.CORPORATE,
    RecordType.MEETING,
]);
/**
 * CZNA-321: targets whose child form PUBLISHES rather than saves a draft, so the
 * reference the parent gets back points at a published record, never at a draft
 * (M. Mahr, 23. 9. 2026). For these the picker offers the affordance only when
 * the backend says a new proposal of that type would be published directly — the
 * `publish` entry of `GET proposals/allowed-actions`, read through
 * `useAllowedProposalActions`, never recomputed here (CZNA-598).
 *
 * GEOGRAPHIC is not in it: its CZNA-241 flow predates that decision and still
 * hands back the draft proposal id, which `publishProposal` re-links on publish.
 */
export var PUBLISHING_PROPOSE_TARGETS = new Set([
    RecordType.PERSONAL,
    RecordType.CORPORATE,
    RecordType.MEETING,
]);
/**
 * CZNA-321: the "minimal create" subset — the tab ids of the type's OWN form-config
 * the child form shows when opened from the picker. Not a second form: the form
 * still holds the state of the whole config (so the hidden tabs' defaults are
 * saved), only the tab bar is narrower, and every later view of the record renders
 * the complete form. An id the config does not carry is simply not shown.
 *
 * `sources` is in it although the ticket's default named only `basic` + `identifiers`:
 * the child PUBLISHES, and the publish gate refuses a PERSONAL record without 670
 * (`NO_FIELD_670`, BLOCKING — `czna-684-submit-from-form.spec.ts` pins it), which lives
 * on that tab. Without it the minimal create could never succeed. The same rule has a
 * CORPORATE / MEETING variant (Aleph FMT=KA, over `11#`), so CZNA-320 takes the same
 * three tabs; both configs carry them under the same ids.
 *
 * Kept here rather than as a form-config attribute on purpose: an attribute would
 * freeze behind `customized=true` and changing it would be a Type-B deploy, while
 * this is the default NK is shown to react to (`otázka-na-NK` on CZNA-321).
 */
export var MINIMAL_CREATE_TABS = (_a = {},
    _a[RecordType.PERSONAL] = ['basic', 'identifiers', 'sources'],
    _a[RecordType.CORPORATE] = ['basic', 'identifiers', 'sources'],
    _a[RecordType.MEETING] = ['basic', 'identifiers', 'sources'],
    _a);
/**
 * CZNA-321 — the tabs a narrowed form must show: the subset plus every tab that
 * carries a field a BLOCKING finding points at. A refused publish names what is
 * missing; if that sits on a hidden tab (040 for a library with no sigla, a rule a
 * tenant tightens later), the cataloguer must be able to reach it, or the minimal
 * create becomes a dead end. Warnings do not widen it — they do not block.
 *
 * Returns `visibleTabIds` itself when nothing needs revealing, so a memoised caller
 * keeps a stable reference.
 */
export var withTabsCarryingErrors = function (config, visibleTabIds, errors) {
    if (!visibleTabIds || !errors || errors.length === 0)
        return visibleTabIds;
    var tags = new Set(errors
        .flatMap(function (issue) { var _a; return (_a = issue.locations) !== null && _a !== void 0 ? _a : [issue.location]; })
        .map(function (location) { return location === null || location === void 0 ? void 0 : location.tag; })
        .filter(function (tag) { return Boolean(tag); }));
    var revealed = config.tabs
        .filter(function (tab) { return !visibleTabIds.includes(tab.id); })
        .filter(function (tab) { return tab.fields.some(function (field) { return tags.has(field.tag); }); })
        .map(function (tab) { return tab.id; });
    return revealed.length > 0 ? __spreadArray(__spreadArray([], visibleTabIds, true), revealed, true) : visibleTabIds;
};
/** sessionStorage key namespace. The token is appended: `czna:proposeGA:<uuid>`. */
var KEY_PREFIX = 'czna:proposeGA:';
/** Generate a flow token. Falls back to a timestamp+random when crypto is absent. */
var newToken = function () {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Non-crypto fallback (old browsers / jsdom without randomUUID) — uniqueness is
    // only needed within a single tab session, so this is sufficient.
    return "".concat(Date.now().toString(36), "-").concat(Math.random().toString(36).slice(2));
};
var keyFor = function (token) { return "".concat(KEY_PREFIX).concat(token); };
/**
 * Remove every pre-existing `czna:proposeGA:*` key from sessionStorage. The flow
 * is inherently single-active per tab (one in-progress propose at a time), so a
 * key surviving from a prior flow can only be an orphan — e.g. the user pressed
 * browser Back (not Cancel) on the GA route, so they returned without `?restore`,
 * the one-shot `loadSnapshot` never ran, and the key leaked. Sweeping here keeps
 * sessionStorage from accumulating dead snapshots. Never throws.
 */
function purgeStaleSnapshots() {
    try {
        var stale = [];
        for (var i = 0; i < sessionStorage.length; i += 1) {
            var key = sessionStorage.key(i);
            if (key && key.startsWith(KEY_PREFIX))
                stale.push(key);
        }
        stale.forEach(function (key) { return sessionStorage.removeItem(key); });
    }
    catch (_a) {
        // sessionStorage unavailable (private mode) — nothing to purge.
    }
}
/**
 * Persist a parent-form snapshot and return its freshly-minted token. The token
 * is threaded into the child route as `?flow=<token>` and later back as
 * `?restore=<token>`.
 */
export function saveSnapshot(input) {
    // Drop any orphaned snapshot from an abandoned prior flow before writing ours.
    purgeStaleSnapshots();
    var token = newToken();
    var snapshot = __assign(__assign({}, input), { token: token });
    try {
        sessionStorage.setItem(keyFor(token), JSON.stringify(snapshot));
    }
    catch (_a) {
        // Quota exceeded / sessionStorage unavailable (private mode). The flow then
        // degrades to "navigate but no restore" — acceptable, same risk class as any
        // unsaved form. We still return the token so navigation can proceed.
    }
    return token;
}
/**
 * Read and DELETE the snapshot for `token` (one-shot). Returns `null` for a
 * missing key or unparseable JSON — never throws, so a malformed/absent key
 * degrades gracefully to "no restore".
 */
export function loadSnapshot(token) {
    if (!token)
        return null;
    var key = keyFor(token);
    var raw = null;
    try {
        raw = sessionStorage.getItem(key);
        // Delete eagerly so even a parse failure cannot leave a stale key behind,
        // and a second load of the same token returns null.
        sessionStorage.removeItem(key);
    }
    catch (_a) {
        return null;
    }
    if (raw == null)
        return null;
    try {
        var parsed = JSON.parse(raw);
        // Minimal shape guard — a corrupt/partial object (e.g. someone hand-edited
        // storage, or a snapshot truncated by a quota error) should not crash the
        // restore. Require `formValues` AND `target` AND `token`: without `target`
        // the write-back would `setValue(undefined, …)`; without these the snapshot
        // is unusable, so degrade to "no restore".
        if (!parsed || typeof parsed !== 'object' || !parsed.formValues || !parsed.target || !parsed.token)
            return null;
        return parsed;
    }
    catch (_b) {
        return null;
    }
}
/**
 * Read the snapshot for `token` WITHOUT deleting it. Used by the child GA create
 * route to learn its parent's `returnTo` while leaving the one-shot read for the
 * parent's {@link loadSnapshot} on return. Returns `null` for missing/corrupt.
 */
export function peekSnapshot(token) {
    if (!token)
        return null;
    try {
        var raw = sessionStorage.getItem(keyFor(token));
        if (raw == null)
            return null;
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || !parsed.formValues || !parsed.target || !parsed.token)
            return null;
        return parsed;
    }
    catch (_a) {
        return null;
    }
}
/**
 * Attach the child GA result to an existing snapshot (does NOT consume it —
 * the parent's {@link loadSnapshot} on return is the one-shot read). No-op if
 * the key is missing/corrupt. Returns true when the result was written.
 */
export function attachResult(token, result) {
    if (!token)
        return false;
    var key = keyFor(token);
    try {
        var raw = sessionStorage.getItem(key);
        if (raw == null)
            return false;
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object')
            return false;
        parsed.result = result;
        sessionStorage.setItem(key, JSON.stringify(parsed));
        return true;
    }
    catch (_a) {
        return false;
    }
}
